import { BaseService } from "../baseService";
import { FileAPIService } from "../fileAPIService";
import { ClaimAPIService } from "./claimAPIService";
import { sanitiseParams } from "src/utils";
import { PostClaimRequestBody } from "src/types/api/claims/postClaim";
import { GetClaimsQueryParams } from "src/types/api/claims/getClaims";
import { GetClaimsStatsResponse } from "src/types/api/claims/getClaimsStats";
import { PatchClaimRequestBody } from "src/types/api/claims/patchClaim";
import { PostClaimApproveRequestBody } from "src/types/api/claims/postClaimApprove";
import { PostClaimRejectRequestBody } from "src/types/api/claims/postClaimReject";
import { PostClaimConfirmRequestBody } from "src/types/api/claims/postClaimConfirm";
import { Claim, PaginatedClaims } from "src/types";

class ClaimService extends BaseService<ClaimAPIService> {
  private readonly fileAPIService: FileAPIService;

  constructor(apiService: ClaimAPIService, fileAPIService: FileAPIService) {
    super(apiService);
    this.fileAPIService = fileAPIService;
  }

  private async handleImageFiles(claimId: string, claimImages: File[], actionContext?: string): Promise<void> {
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const actionSuffix = actionContext ? `_${actionContext}` : "";
    const claimImagesNames = claimImages.map((file, index) => {
      const originalName = file.name;
      const extension = originalName.substring(originalName.lastIndexOf("."));
      const baseName = originalName.substring(0, originalName.lastIndexOf("."));

      const prefix = `${timestamp}${actionSuffix}_${index}_`;
      const maxBaseNameLength = 60 - prefix.length - extension.length;

      const truncatedBaseName =
        baseName.length > maxBaseNameLength ? baseName.substring(0, maxBaseNameLength) : baseName;

      const fileName = `${prefix}${truncatedBaseName}${extension}`;

      return fileName.length > 60 ? fileName.substring(0, 60 - extension.length) + extension : fileName;
    });

    const signedPutUrls = await this.apiService.postClaimImagesUrls(claimId, {
      operation: "PUT",
      fileNames: claimImagesNames
    });

    for (let i = 0; i < signedPutUrls.length; i++) {
      const { signedUrl, fileName } = signedPutUrls[i];
      const file = claimImages[i];

      if (file && fileName === claimImagesNames[i]) {
        await this.fileAPIService.putFile(file, signedUrl);
      }
    }
  }

  @sanitiseParams()
  async getClaims(queryParams: GetClaimsQueryParams): Promise<PaginatedClaims> {
    const paginatedClaims = await this.apiService.getClaims(queryParams);
    return paginatedClaims;
  }

  async getClaimsStats(): Promise<GetClaimsStatsResponse> {
    return await this.apiService.getClaimsStats();
  }

  @sanitiseParams()
  async submitClaim(requestBody: PostClaimRequestBody, claimImages?: File[], vendorName?: string): Promise<Claim> {
    let claimId: string | undefined;
    if (vendorName && claimImages?.length) {
      const { ClaimHelper } = await import("src/utils/claimHelper");
      claimId = ClaimHelper.generateClaimId(vendorName);
      requestBody.id = claimId;
      await this.handleImageFiles(claimId, claimImages);
    }

    const claim = await this.apiService.postClaim(requestBody);

    return claim;
  }

  async getClaimById(claimId: string): Promise<Claim> {
    return await this.apiService.getClaimById(claimId);
  }

  async updateClaim(
    claimId: string,
    requestBody: PatchClaimRequestBody,
    newImages?: File[],
    deletedImageUrls?: string[]
  ): Promise<Claim> {
    if (deletedImageUrls?.length) {
      await this.archiveClaimImages(claimId, deletedImageUrls, "edit");
    }

    if (newImages?.length) {
      await this.handleImageFiles(claimId, newImages, "edit");
    }

    const claim = await this.apiService.patchClaim(claimId, requestBody);

    return claim;
  }

  async approveClaim(
    claimId: string,
    requestBody?: PostClaimApproveRequestBody,
    newImages?: File[],
    deletedImageUrls?: string[]
  ): Promise<Claim> {
    if (deletedImageUrls?.length) {
      await this.archiveClaimImages(claimId, deletedImageUrls, "approve");
    }

    if (newImages?.length) {
      await this.handleImageFiles(claimId, newImages, "approve");
    }

    const claim = await this.apiService.postClaimApprove(claimId, requestBody);

    return claim;
  }

  async rejectClaim(claimId: string, rejectionReason?: string): Promise<Claim> {
    const requestBody: PostClaimRejectRequestBody = { rejectionReason };
    return await this.apiService.postClaimReject(claimId, requestBody);
  }

  async confirmClaim(claimId: string, confirmationMessage: string, creditFiles?: File[]): Promise<Claim> {
    if (creditFiles && creditFiles.length > 0) {
      await this.handleCreditFiles(claimId, creditFiles);
    }

    const requestBody: PostClaimConfirmRequestBody = {
      confirmationMessage: confirmationMessage.trim()
    };
    return await this.apiService.postClaimConfirm(claimId, requestBody);
  }

  private async handleCreditFiles(claimId: string, creditFiles: File[]): Promise<void> {
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
    const creditFileNames = creditFiles.map((file, index) => {
      const originalName = file.name;
      const extension = originalName.substring(originalName.lastIndexOf("."));
      const baseName = originalName.substring(0, originalName.lastIndexOf("."));

      const prefix = `${timestamp}_${index}_`;
      const maxBaseNameLength = 60 - prefix.length - extension.length;

      const truncatedBaseName =
        baseName.length > maxBaseNameLength ? baseName.substring(0, maxBaseNameLength) : baseName;

      const fileName = `${prefix}${truncatedBaseName}${extension}`;

      return fileName.length > 60 ? fileName.substring(0, 60 - extension.length) + extension : fileName;
    });

    const signedPutUrls = await this.apiService.postClaimCreditUrls(claimId, {
      operation: "PUT",
      fileNames: creditFileNames
    });

    for (let i = 0; i < signedPutUrls.length; i++) {
      const { signedUrl, fileName } = signedPutUrls[i];
      const file = creditFiles[i];

      if (file && fileName === creditFileNames[i]) {
        await this.fileAPIService.putFile(file, signedUrl);
      }
    }
  }

  async getClaimImageUrls(claimId: string): Promise<string[]> {
    const signedUrls = await this.apiService.postClaimImagesUrls(claimId, {
      operation: "GET",
      fileNames: []
    });

    return signedUrls.map(({ signedUrl }) => signedUrl);
  }

  async getClaimCreditFiles(claimId: string): Promise<File[]> {
    const creditFiles: File[] = [];

    const signedGETUrls = await this.apiService.postClaimCreditUrls(claimId, { operation: "GET" });

    for (const { signedUrl, fileName } of signedGETUrls) {
      const fileBlob = await this.fileAPIService.getFile(signedUrl);
      const creditFile = new File([fileBlob], fileName, {
        type: fileBlob.type,
        lastModified: new Date().getTime()
      });

      creditFiles.push(creditFile);
    }

    return creditFiles;
  }

  private async archiveClaimImages(claimId: string, imageUrls: string[], actionContext?: string): Promise<void> {
    const fileNames = imageUrls.map((url) => {
      const urlParts = url.split("/");
      return urlParts[urlParts.length - 1].split("?")[0];
    });

    if (fileNames.length > 0) {
      await this.apiService.postClaimImagesArchive(claimId, {
        fileNames,
        actionContext
      });
    }
  }
}

export { ClaimService };
